import type { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import axios, { AxiosHeaders } from 'axios';
import { apiBaseUrl, appVersion, appVersionHash, stage } from 'constants/config';
import isString from 'lodash/isString';
import isArray from 'lodash/isArray';
import { getReasonPhrase, StatusCodes } from 'http-status-codes';
import type ServiceContainer from 'services/service-container';
import type { NotificationArgs } from 'types/notifications.types';
import { NotificationTypes } from 'types/notifications.types';
import axiosRetry, { exponentialDelay, isSafeRequestError } from 'axios-retry';
import { recentlyUpdatedProjectsKey, recentProjectIdsLimit } from 'constants/local-storage.constants';
import { LAST_VISITED_ORGANIZATION_AND_USER_ID_STORAGE_KEY } from 'stores/mobx/organizations.store';
import BrowserStorage from 'services/BrowserStorage';

export type HttpClient = ReturnType<typeof createHttpClient>;

// how a caller wants a failure surfaced — never affects the response, so it belongs to the call site
export type ErrorSurfacingOptions = Pick<AxiosRequestConfig, 'hideNotification' | 'hidePayGateModal'>;

export const isGetRequest = (method?: string) => method && ['get', 'GET'].includes(method);

// invite tokens and the like travel in the path, and this string ends up on screen
export const opaqueSecretSegment = /^[0-9a-f]{32,}$/i;

// the base only matters for a relative url - either way `pathname` drops the origin and the query
const relativeUrlBase = 'http://relative.invalid';

const getRequestPath = (config?: AxiosRequestConfig): string => {
  const { pathname } = new URL(config?.url ?? '', relativeUrlBase);

  return pathname
    .split('/')
    .map(segment => (opaqueSecretSegment.test(segment) ? '***' : segment))
    .join('/');
};

const getReasonPhraseSafe = (statusCode: number): string => {
  try {
    return getReasonPhrase(statusCode);
  } catch {
    return '';
  }
};

// the context line for the global error toast: which call failed and with what status
export const getApiErrorDescriptor = (
  error: AxiosError
): { id: string; values: Record<string, string | number> } | undefined => {
  const { config, response } = error;
  if (!config?.url) return undefined;

  const method = (config.method ?? 'GET').toUpperCase();
  const path = getRequestPath(config);

  if (!response) return { id: 'api.error.request-context.no-response', values: { method, path } };

  const status = response.status;
  const statusText = getReasonPhraseSafe(status);

  if (!statusText) return { id: 'api.error.request-context.no-status-text', values: { method, path, status } };

  return { id: 'api.error.request-context', values: { method, path, status, statusText } };
};

export const createHttpClient = () => {
  const axiosClient = axios.create();
  axiosClient.defaults.baseURL = apiBaseUrl;
  // only AxiosError.toJSON() reads this: without it a serialised error carries the bearer header into logs
  axiosClient.defaults.redact = ['Authorization'];
  axiosClient.defaults.headers.common['X-Env0-Client-Version'] = appVersion ?? appVersionHash ?? stage;
  axiosClient.defaults.headers.common['X-Env0-Monitor'] = 'true';

  return axiosClient;
};

export const axiosClient = createHttpClient();

export function postConfigAxiosClient(axiosClient: HttpClient, service: ServiceContainer) {
  // the single retry policy — registering axios-retry twice makes the registrations share
  // per-request state and clobber each other's options, so everything lives in one condition
  axiosRetry(axiosClient, {
    retries: 3,
    retryDelay: exponentialDelay,
    retryCondition: async error => {
      if (axios.isCancel(error) || error.code === 'ERR_CANCELED') return false;

      if (error.response?.status === StatusCodes.PAYMENT_REQUIRED) return false;

      if (error.response?.status === StatusCodes.UNAUTHORIZED) {
        // a 401 usually means the access token expired: renew the session once and retry with
        // the fresh token. the flag rides on the config, which axios-retry carries across
        // attempts, so a second 401 for the same request is a real authorization failure —
        // give up without renewing again (and without a toast; 401s stay silent)
        if (error.config?.didRenewSession) return false;
        if (error.config) error.config.didRenewSession = true;

        await service.authStore.silentRenewSession();

        return true;
      }

      // a request that already ran out of server time would only make the user wait that long again
      if (error.config?.skipRetryOnTimeout && error.response?.status === StatusCodes.GATEWAY_TIMEOUT) return false;

      // GET-only: transient failures (network / 5xx) retry before anything is surfaced to the user
      return isSafeRequestError(error);
    },
    onRetry: (retryCount, error, requestConfig) => {
      // never serialise the error: its config carries the Authorization header and the request body,
      // and console output reaches Sentry. the path is masked for the same reason: invite tokens travel in it
      console.info(
        `${requestConfig.method} ${getRequestPath(requestConfig)} returned ${error.response?.status ?? error.code ?? error.message}, retrying (attempt ${retryCount})`
      );
    }
  });

  // surfaces the failure only once the retry layer above has given up.
  // a retried request re-enters the whole interceptor chain, so the final rejection passes
  // through here once per attempt — the flag keeps it to one notification per error
  axiosClient.interceptors.response.use(undefined, (error: AxiosError) => {
    const status = error.response?.status;
    const silent =
      axios.isCancel(error) ||
      error.code === 'ERR_CANCELED' ||
      status === StatusCodes.UNAUTHORIZED ||
      status === StatusCodes.PAYMENT_REQUIRED ||
      error.config?.hideNotification ||
      error.notificationShown;

    if (!silent) {
      error.notificationShown = true;
      const descriptor = getApiErrorDescriptor(error);
      service.notificationStore.setNotification({
        notificationType: NotificationTypes.error,
        ...getApiErrorNotification(error),
        description: descriptor?.id,
        descriptionVariables: descriptor?.values
      });
    }

    throw error;
  });

  // this interceptor is for tracking the last visited projects
  // it saves the project ids of projects that some action was done in their page
  // action needs to be different from get
  axiosClient.interceptors.request.use(config => {
    if (config.method === 'get') {
      return config;
    }

    const pathname = window.location.pathname;

    // if the pathname is like /p/<projectId> I want to get the id
    const projectId = pathname.match(/\/p\/([^/]+)/)?.[1];

    if (!projectId) {
      return config;
    }

    const lastVisited = BrowserStorage.getItem(LAST_VISITED_ORGANIZATION_AND_USER_ID_STORAGE_KEY);
    const organizationId = lastVisited?.organization?.id;
    if (!organizationId) {
      return config;
    }
    const recentProjectIdsForAllOrganizations = BrowserStorage.getItem(recentlyUpdatedProjectsKey) ?? {};
    const recentProjectIds = recentProjectIdsForAllOrganizations[organizationId] ?? [];

    const index = recentProjectIds.indexOf(projectId);
    if (index !== -1) {
      // if exists in the array, make first in the array
      recentProjectIds.splice(index, 1);
      recentProjectIds.unshift(projectId);
    } else {
      recentProjectIds.unshift(projectId);
    }

    if (recentProjectIds.length > recentProjectIdsLimit) {
      recentProjectIds.pop();
    }

    BrowserStorage.setItem(recentlyUpdatedProjectsKey, {
      ...recentProjectIdsForAllOrganizations,
      [organizationId]: recentProjectIds
    });

    return config;
  });

  axiosClient.interceptors.request.use(config => {
    if (!config.headers) config.headers = new AxiosHeaders();
    config.headers.Authorization = `Bearer ${service.authStore.getAccessToken()}`;

    if (isGetRequest(config.method)) {
      config.sourcePageRoute = window.location.pathname + window.location.search;
    }

    return config;
  });

  axiosClient.interceptors.response.use(
    config => {
      return config;
    },
    (error: AxiosError) => {
      if (error?.response?.status === StatusCodes.PAYMENT_REQUIRED) {
        // callers that render their own in-page pay-gate state opt out of the global modal,
        // otherwise the user gets two stacked "upgrade your tier" CTAs
        const { hidePayGateModal, sourcePageRoute, method } = error.config ?? {};

        if (!hidePayGateModal && sourcePageRoute && isGetRequest(method)) {
          service.payGateStore.cachePageRoute(sourcePageRoute, error);
        }

        service.payGateStore.cacheOrThrowError(error);

        if (!hidePayGateModal) service.payGateStore.setActivePayGateError(error);
      }
      throw error;
    }
  );
}

export const returnData = <T>(response: AxiosResponse<T>): T => response.data;

export const getResponseErrorStatus = (err: AxiosError): number | null => {
  return err.response ? err.response.status : null;
};

export const getResponseErrorData = (err: AxiosError) => {
  return (err.response ? err.response.data : '') as string;
};

export const getTransportErrorMessageId = (error: AxiosError): string | undefined => {
  if (error.response) return undefined;
  if (error.code === 'ERR_NETWORK') return 'api.error.network';
  if (error.code === 'ECONNABORTED') return 'api.error.timeout';

  return undefined;
};

export const getApiErrorText = (error: AxiosError<any>): string => {
  let message = '';

  if (!error.response) {
    const transportMessageId = getTransportErrorMessageId(error);

    if (transportMessageId) return transportMessageId;

    message = error.message;
  } else if (!error.response.data) {
    message = error.message;
  } else {
    const { data } = error.response;
    if (isArray(data)) data.forEach(line => (message += `${line}\n`));
    else if (isString(data)) message += `${data}\n`;
    else if (data.message) message += `${data.message}\n`;
  }

  return message;
};

export const getApiErrorToastMessage = (error: AxiosError<any>): string =>
  getApiErrorText(error) || 'api.error.request-failed';

// names the resource a failing GET was loading, so the toast can say what broke.
// curated from the most frequent failing endpoints in Sentry (prod) — extend as new offenders show up.
// order matters: a specific pattern goes above the collection it belongs to.
const apiResourcesByUrlPattern: Array<[RegExp, string]> = [
  [/^blueprints\/vcs-revisions/, 'api.resource.templateRevisions'],
  [/^blueprints/, 'api.resource.templates'],
  [/^modules/, 'api.resource.modules'],
  [/^permissions\/user/, 'api.resource.userPermissions'],
  [/^projects/, 'api.resource.projects'],
  [/^policies/, 'api.resource.policies'],
  [/^environments\/runs-usage/, 'api.resource.usageData'],
  [/^environment-outputs/, 'api.resource.environmentOutputs'],
  [/^environments/, 'api.resource.environments'],
  [/^deployments\/[^/]+\/steps/, 'api.resource.deploymentSteps'],
  [/^deployments/, 'api.resource.deployments'],
  [/^organizations\/[^/]+\/users/, 'api.resource.organizationUsers'],
  [/^costs\//, 'api.resource.costData'],
  [/^configuration-sets/, 'api.resource.configurationSets'],
  [/^configuration/, 'api.resource.configurationVariables'],
  [/^dashboards/, 'api.resource.dashboards']
];

export const getApiErrorResourceKey = (config?: AxiosRequestConfig): string | undefined => {
  if (!isGetRequest(config?.method) || !config?.url) return undefined;

  const url = config.url.replace(/^\//, '');
  return apiResourcesByUrlPattern.find(([pattern]) => pattern.test(url))?.[1];
};

export const getApiErrorNotification = (error: AxiosError): Pick<NotificationArgs, 'message' | 'messageVariables'> => {
  const message = getApiErrorText(error).trim();
  const resource = getApiErrorResourceKey(error.config);

  if (!resource || !message) return { message: getApiErrorToastMessage(error) };

  return {
    message: 'api.error.failedToLoad',
    messageVariables: { resource, details: message }
  };
};
